import React, { useRef, useEffect, useState } from 'react';

interface Props {
  html: string;
  className?: string;
}

/**
 * Renders admin-authored branded HTML (custom header/footer) inside a
 * sandboxed iframe so the full document structure, <style> blocks, and
 * Google Font <link> tags are preserved without leaking into the host page.
 *
 * sandbox="allow-same-origin" lets the parent read contentDocument for
 * auto-height sizing while blocking script execution.
 */
const BrandedHtmlFrame: React.FC<Props> = ({ html, className }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    let resizeObserver: ResizeObserver | null = null;

    const syncHeight = () => {
      const doc = iframe.contentDocument;
      if (!doc) return;

      const body = doc.body;
      const docEl = doc.documentElement;
      if (body && docEl) {
        const h = Math.max(
          body.scrollHeight,
          body.offsetHeight,
          docEl.scrollHeight,
          docEl.offsetHeight
        );
        setHeight(h);
      }
    };

    const handleLoad = () => {
      const doc = iframe.contentDocument;
      const body = doc?.body;
      const docEl = doc?.documentElement;
      if (body) {
        body.style.margin = '0';
        body.style.padding = '0';
        body.style.overflow = 'hidden';
        body.style.background = 'transparent';
      }
      if (docEl) {
        docEl.style.background = 'transparent';
      }

      // Make wrapper elements fill the iframe width
      if (body) {
        const firstChild = body.firstElementChild as HTMLElement | null;
        if (firstChild) {
          firstChild.style.maxWidth = '100%';
          firstChild.style.width = '100%';
          firstChild.style.borderRadius = '0';
          firstChild.style.border = 'none';
        }
      }

      syncHeight();
      if (body) {
        resizeObserver = new ResizeObserver(syncHeight);
        resizeObserver.observe(body);
      }
    };

    iframe.addEventListener('load', handleLoad);
    return () => {
      iframe.removeEventListener('load', handleLoad);
      resizeObserver?.disconnect();
    };
  }, [html]);

  return (
    <iframe
      ref={iframeRef}
      srcDoc={html}
      className={className}
      scrolling="no"
      style={{
        width: '100%',
        height: height > 0 ? `${height}px` : 'auto',
        border: 'none',
        overflow: 'hidden',
        display: 'block'
      }}
      sandbox="allow-same-origin"
      title="Branded content"
    />
  );
};

export default BrandedHtmlFrame;
